import { useState, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  uploadMediaAsset,
  assignProductMedia,
  assignOptionValueMedia,
  assignVariantMedia,
  removeProductMediaAssignment,
  removeOptionValueMediaAssignment,
  removeVariantMediaAssignment,
  updateMediaAltText,
  updateMediaRole,
  signMediaUrls,
  SIGNED_MEDIA_REFRESH_MS,
  validateFile,
  MEDIA_ROLE_LABELS,
  uniqueOptionValueMedia,
  reorderedMediaIds,
  type MediaRole,
  type MediaAsset,
  type ProductMedia,
  type OptionValueMedia,
  type VariantMedia,
} from "@/lib/media";
import { Modal } from "@/components/base/Modal";
import * as productApi from "@/lib/api/products";
import { firstRelation } from "@/lib/relations";

interface OptionValue {
  id: string;
  value: string;
  position: number;
  option_name: string;
}

interface VariantMini {
  id: string;
  sku: string | null;
  option_values: Record<string, string> | null;
}

interface Props {
  productId: string;
  orgId: string;
  variants: VariantMini[];
}

const ROLE_OPTIONS: MediaRole[] = ["gallery", "detail", "lifestyle", "packaging", "size_chart", "swatch"];

function DropZone({ label, onDrop }: { label: string; onDrop: (files: FileList | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onDrop(e.dataTransfer.files); }}
      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50"}`}
      onClick={() => inputRef.current?.click()}
    >
      <i className="ri-upload-cloud-2-line text-2xl text-slate-400 mb-2 block" />
      <p className="text-sm text-slate-600 font-medium">{label}</p>
      <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP · Max 10 MB each</p>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => onDrop(e.target.files)}
      />
    </div>
  );
}

export default function MediaTab({ productId, orgId, variants }: Props) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>();
  const [confirmArchiveId, setConfirmArchiveId] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [altDrafts, setAltDrafts] = useState<Record<string, string>>({});

  const { data: productMedia, isLoading: pmLoading } = useQuery({
    queryKey: ["product-media", orgId, productId],
    enabled: !!orgId && !!productId,
    staleTime: SIGNED_MEDIA_REFRESH_MS / 2,
    refetchInterval: SIGNED_MEDIA_REFRESH_MS,
    queryFn: async () => {
      const { data } = await supabase
        .from("product_media")
        .select("id, role, position, is_primary, alt_text, status, media_assets(id, storage_key, mime_type, width, height, original_filename, file_size, checksum)")
        .eq("org_id", orgId)
        .eq("product_id", productId)
        .eq("status", "active")
        .order("is_primary", { ascending: false })
        .order("position");
      const rows = (data ?? []).map((row) => ({ ...row, media_assets: firstRelation(row.media_assets) })) as ProductMedia[];
      const keys = rows.map((r) => r.media_assets?.storage_key).filter((k): k is string => !!k);
      const urls = keys.length ? await signMediaUrls(keys) : {};
      setSignedUrls((prev) => ({ ...prev, ...urls }));
      return rows;
    },
  });

  const { data: optionsData } = useQuery({
    queryKey: ["product-options-media", orgId, productId],
    enabled: !!orgId && !!productId,
    staleTime: SIGNED_MEDIA_REFRESH_MS / 2,
    refetchInterval: SIGNED_MEDIA_REFRESH_MS,
    queryFn: async () => {
      const { data } = await supabase
        .from("product_options")
        .select("id, name, position, product_option_values(id, value, position, status)")
        .eq("org_id", orgId)
        .eq("product_id", productId)
        .eq("status", "active")
        .order("position");
      const options = (data ?? []).map((row) => ({ ...row, product_option_values: row.product_option_values ?? [] })) as { id: string; name: string; position: number; product_option_values: { id: string; value: string; position: number; status: string }[] }[];
      const allValueIds: string[] = [];
      const valueMap = new Map<string, OptionValue>();
      options.forEach((o) =>
        o.product_option_values
          .filter((v) => v.status === "active")
          .forEach((v) => {
            allValueIds.push(v.id);
            valueMap.set(v.id, { ...v, option_name: o.name });
          }),
      );
      if (allValueIds.length === 0) return { options, valueMap, ovMedia: [] };
      const { data: ovData } = await supabase
        .from("option_value_media")
        .select("id, product_option_value_id, role, position, is_primary, alt_text, media_assets(id, storage_key, width, height, original_filename, file_size)")
        .eq("org_id", orgId)
        .in("product_option_value_id", allValueIds)
        .eq("status", "active")
        .order("position");
      const ovMedia = (ovData ?? []).map((row) => ({ ...row, media_assets: firstRelation(row.media_assets) })) as OptionValueMedia[];
      const keys = ovMedia.map((r) => r.media_assets?.storage_key).filter((k): k is string => !!k);
      const urls = keys.length ? await signMediaUrls(keys) : {};
      setSignedUrls((prev) => ({ ...prev, ...urls }));
      return { options, valueMap, ovMedia };
    },
  });

  const { data: variantMediaData } = useQuery({
    queryKey: ["variant-media", orgId, productId],
    enabled: !!orgId && !!productId && variants.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("variant_media")
        .select("id, variant_id, role, position, is_primary, alt_text, media_assets(id, storage_key, width, height, original_filename, file_size)")
        .eq("org_id", orgId)
        .in("variant_id", variants.map((v) => v.id))
        .eq("status", "active")
        .order("position");
      const vm = (data ?? []).map((row) => ({ ...row, media_assets: firstRelation(row.media_assets) })) as VariantMedia[];
      const keys = vm.map((r) => r.media_assets?.storage_key).filter((k): k is string => !!k);
      const urls = keys.length ? await signMediaUrls(keys) : {};
      setSignedUrls((prev) => ({ ...prev, ...urls }));
      return vm;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["product-media", orgId, productId] });
    queryClient.invalidateQueries({ queryKey: ["product-options-media", orgId, productId] });
    queryClient.invalidateQueries({ queryKey: ["variant-media", orgId, productId] });
  }, [orgId, productId, queryClient]);

  const handleFiles = useCallback(
    async (files: FileList | null, target: { type: "product"; id: string } | { type: "option_value"; id: string } | { type: "variant"; id: string }) => {
      if (!files || files.length === 0) return;
      setUploading(true);
      setUploadErrors([]);
      const errors: string[] = [];
      let position = 0;
      if (target.type === "product") {
        position = productMedia?.length ?? 0;
      } else if (target.type === "option_value") {
        position = optionsData?.ovMedia.filter((m) => m.product_option_value_id === target.id).length ?? 0;
      } else {
        position = variantMediaData?.filter((m) => m.variant_id === target.id).length ?? 0;
      }
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const err = validateFile(file);
        if (err) {
          errors.push(`${file.name}: ${err}`);
          continue;
        }
        try {
          const asset = await uploadMediaAsset(orgId, productId, file);
          const url = await signMediaUrls([asset.storage_key]);
          setSignedUrls((prev) => ({ ...prev, ...url }));
          if (target.type === "product") {
            await assignProductMedia(orgId, productId, asset.id, "gallery", "", position + i, i === 0 && position === 0);
          } else if (target.type === "option_value") {
            await assignOptionValueMedia(orgId, target.id, asset.id, "gallery", "", position + i);
          } else {
            await assignVariantMedia(orgId, target.id, asset.id, "gallery", "", position + i);
          }
        } catch (e) {
          errors.push(`${file.name}: ${(e as Error).message}`);
        }
      }
      setUploadErrors(errors);
      setUploading(false);
      invalidate();
    },
    [orgId, productId, productMedia, optionsData, variantMediaData, invalidate],
  );

  const setPrimary = async (pmId: string) => {
    await productApi.setPrimaryMedia(orgId, productId, pmId);
    invalidate();
  };

  const move = async (table: "product_media" | "variant_media" | "option_value_media", items: { id: string; position: number }[], fromIdx: number, toIdx: number) => {
    // Reorder the list that actually backs the card being moved. Using another
    // list's rows here would write positions to the wrong table by the wrong ids.
    const ids = reorderedMediaIds(items, fromIdx, toIdx);
    if (ids.length === 0) return;
    if (table === "product_media") {
      await productApi.reorderProductMedia(orgId, productId, ids);
    } else {
      for (let i = 0; i < ids.length; i++) {
        await supabase.from(table).update({ position: i }).eq("id", ids[i]).eq("org_id", orgId);
      }
    }
    invalidate();
  };

  const archiveAsset = async (assetId: string) => {
    setArchiving(true);
    setArchiveError(null);
    try {
      await productApi.archiveMediaAsset(orgId, assetId);
    } catch (e) {
      setArchiving(false);
      setArchiveError((e as Error).message);
      return;
    }
    setArchiving(false);
    setConfirmArchiveId(null);
    invalidate();
  };

  const renderMediaCard = (
    item: ProductMedia | OptionValueMedia | VariantMedia,
    table: "product_media" | "variant_media" | "option_value_media",
    idx: number,
    total: number,
    onSetPrimary?: () => void,
    reorderItems: { id: string; position: number }[] = [],
  ) => {
    const asset = item.media_assets;
    const url = asset?.storage_key ? signedUrls?.[asset.storage_key] : undefined;
    return (
      <div key={item.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
        <div className="relative aspect-square bg-slate-50 flex items-center justify-center">
          {url ? (
            <img src={url} alt={item.alt_text || asset?.original_filename || ""} className="w-full h-full object-cover" />
          ) : (
            <div className="w-10 h-10 bg-slate-200 rounded-full" />
          )}
          {item.is_primary && (
            <span className="absolute top-2 left-2 text-[10px] font-semibold bg-emerald-600 text-white px-2 py-0.5 rounded-full">Primary</span>
          )}
          <span className="absolute top-2 right-2 text-[10px] bg-slate-900/70 text-white px-2 py-0.5 rounded-full">{MEDIA_ROLE_LABELS[item.role as MediaRole] ?? item.role}</span>
        </div>
        <div className="p-3 space-y-2 flex-1 flex flex-col">
          <p className="text-xs text-slate-500 truncate" title={asset?.original_filename}>{asset?.original_filename ?? "—"}</p>
          <p className="text-[10px] text-slate-400">{asset?.width ?? "—"}×{asset?.height ?? "—"} · {(asset?.file_size ? (asset.file_size / 1024).toFixed(1) : "—")} KB</p>
          <input
            value={altDrafts[item.id] ?? item.alt_text ?? ""}
            onChange={(e) => setAltDrafts((d) => ({ ...d, [item.id]: e.target.value }))}
            onBlur={() => {
              const draft = altDrafts[item.id];
              if (draft !== undefined && draft !== (item.alt_text ?? "")) {
                updateMediaAltText(table, orgId, item.id, draft).then(invalidate);
              }
              setAltDrafts((d) => {
                const next = { ...d };
                delete next[item.id];
                return next;
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
            }}
            placeholder="Alt text..."
            className="w-full px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs"
          />
          <div className="flex items-center gap-1 mt-auto pt-2">
            <select
              value={item.role}
              onChange={async (e) => {
                await updateMediaRole(table, orgId, item.id, e.target.value as MediaRole);
                invalidate();
              }}
              className="text-xs bg-slate-50 border border-slate-200 rounded px-1 py-1"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>{MEDIA_ROLE_LABELS[r]}</option>
              ))}
            </select>
            {onSetPrimary && !item.is_primary && (
              <button onClick={onSetPrimary} className="text-xs text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded whitespace-nowrap">Set primary</button>
            )}
            <div className="flex-1" />
            <button
              onClick={() => move(table, reorderItems, idx, idx - 1)}
              disabled={idx === 0}
              className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30"
            >
              <i className="ri-arrow-up-line text-xs" />
            </button>
            <button
              onClick={() => move(table, reorderItems, idx, idx + 1)}
              disabled={idx === total - 1}
              className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30"
            >
              <i className="ri-arrow-down-line text-xs" />
            </button>
            <button
              onClick={async () => {
                if (table === "product_media") await removeProductMediaAssignment(orgId, item.id);
                else if (table === "variant_media") await removeVariantMediaAssignment(orgId, item.id);
                else await removeOptionValueMediaAssignment(orgId, item.id);
                invalidate();
              }}
              className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
              title="Remove from this entity"
            >
              <i className="ri-close-line text-xs" />
            </button>
            <button
              onClick={() => { setArchiveError(null); setConfirmArchiveId(asset?.id ?? null); }}
              className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
              title="Permanently delete asset"
            >
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (pmLoading) return <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">Loading media...</div>;

  const pm = productMedia ?? [];

  return (
    <div className="space-y-8">
      {/* Upload */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Product Gallery</h3>
        <DropZone label="Drag images here or click to browse" onDrop={(files) => handleFiles(files, { type: "product", id: productId })} />
        {uploading && <p className="text-xs text-slate-500 mt-2"><i className="ri-loader-4-line animate-spin mr-1" />Uploading...</p>}
        {uploadErrors.length > 0 && (
          <div className="mt-3 space-y-1">
            {uploadErrors.map((e, i) => (
              <p key={i} className="text-xs text-red-600">{e}</p>
            ))}
          </div>
        )}
      </div>

      {/* Product gallery grid */}
      {pm.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <i className="ri-image-line text-3xl text-slate-200 mb-2 block" />
          <p className="text-sm text-slate-500">No product images yet. Upload your first image above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {pm.map((item, idx) =>
            renderMediaCard(item, "product_media", idx, pm.length, () => setPrimary(item.id), pm),
          )}
        </div>
      )}

      {/* Option Value Images */}
      {optionsData && optionsData.options.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-800">Option Value Images</h3>
          <p className="text-xs text-slate-500">Images assigned to an option value (e.g., Colour = Black) are inherited by all variants that use that value.</p>
          {optionsData.options.map((opt) => (
            <div key={opt.id}>
              <p className="text-xs font-semibold text-slate-600 mb-2">{opt.name}</p>
              <div className="space-y-3">
                {opt.product_option_values
                  .filter((v) => v.status === "active")
                  .map((val) => {
                    const related = uniqueOptionValueMedia(
                      optionsData.ovMedia.filter((m) => m.product_option_value_id === val.id),
                    );
                    return (
                      <div key={val.id} className="border border-slate-100 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-slate-700">{val.value}</span>
                          <DropZone label="Add image" onDrop={(files) => handleFiles(files, { type: "option_value", id: val.id })} />
                        </div>
                        {related.length === 0 ? (
                          <p className="text-xs text-slate-400">No images assigned to this option value.</p>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {related.map((m, i) => renderMediaCard(m, "option_value_media", i, related.length, undefined, related))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Variant Overrides */}
      {variants.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-800">Variant Media Overrides</h3>
          <p className="text-xs text-slate-500">Assign images directly to specific variants. These override inherited option-value and product-level images for this variant only.</p>
          <div className="space-y-3">
            {variants.map((v) => {
              const related = (variantMediaData ?? []).filter((m) => m.variant_id === v.id);
              const label = Object.entries(v.option_values ?? {})
                .map(([k, val]) => `${k}: ${val}`)
                .join(" / ") || v.sku || "—";
              return (
                <div key={v.id} className="border border-slate-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-700 font-mono">{label}</span>
                    <DropZone label="Add image" onDrop={(files) => handleFiles(files, { type: "variant", id: v.id })} />
                  </div>
                  {related.length === 0 ? (
                    <p className="text-xs text-slate-400">No variant-specific images.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {related.map((m, i) => renderMediaCard(m, "variant_media", i, related.length, undefined, related))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Archive confirm modal */}
      <Modal
        open={!!confirmArchiveId}
        onClose={() => { setConfirmArchiveId(null); setArchiveError(null); }}
        title="Permanently delete media asset?"
        footer={
          <>
            <button onClick={() => { setConfirmArchiveId(null); setArchiveError(null); }} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm whitespace-nowrap">Cancel</button>
            <button onClick={() => confirmArchiveId && archiveAsset(confirmArchiveId)} disabled={archiving} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm whitespace-nowrap disabled:opacity-60">
              {archiving ? "Deleting..." : "Delete permanently"}
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-600">This removes the file from storage. If the asset is still assigned anywhere, the deletion will be blocked.</p>
        {archiveError && (
          <p className="mt-3 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{archiveError}</p>
        )}
      </Modal>

    </div>
  );
}
